import styled from "styled-components";
import { tablet } from "styles/breakpoints";

export const InterestCardContainer = styled.div<{
  $riskLevel?: "high" | "average" | "unknown";
}>`
  position: relative;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  width: 100%;
  border: 2px solid ${({ $riskLevel }) => {
    switch ($riskLevel) {
      case "high":
        return "#EBC8CC";
      case "average":
        return "#D1DFCE";
      case "unknown":
        return "#E5E7EB";
      default:
        return "#dedee3";
    }
  }};
`;

// Header Section
export const HeaderSection = styled.div<{
  $riskLevel?: "high" | "average" | "unknown";
  $hasRiskLabel?: boolean;
}>`
  padding: 16px;
  padding-top: ${({ $hasRiskLabel }) => ($hasRiskLabel ? "44px" : "16px")};

  background-color: ${({ $riskLevel }) => {
    switch ($riskLevel) {
      case "high":
        return "#F6EEEF";
      case "average":
        return "#EDF0EC";
      case "unknown":
        return "#D6D6D6";
      default:
        return "#f5f5f7";
    }
  }};

  @media (min-width: ${tablet}px) {
    padding: 24px;
    padding-top: ${({ $hasRiskLabel }) => ($hasRiskLabel ? "52px" : "24px")};
  }
`;

export const RiskLevelIndicator = styled.div<{
  $level: "high" | "average" | "unknown";
}>`
  position: absolute;
  top: -2px;
  left: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 8px 0 8px 0;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
  z-index: 1;
  color: #ffffff;

  @media (min-width: ${tablet}px) {
    padding: 10px 20px;
  }

  background-color: ${({ $level }) => {
    switch ($level) {
      case "high":
        return "#8b0000"; /* Dark red */
      case "average":
        return "#2e7d32"; /* Dark green */
      case "unknown":
        return "#616161"; /* Dark grey */
      default:
        return "#616161";
    }
  }};

  /* Triangular tail protruding from bottom left */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 8px 10px 0 0;
    border-color: ${({ $level }) => {
      switch ($level) {
        case "high":
          return "#8b0000 transparent transparent transparent";
        case "average":
          return "#2e7d32 transparent transparent transparent";
        case "unknown":
          return "#616161 transparent transparent transparent";
        default:
          return "#616161 transparent transparent transparent";
      }
    }};
  }
`;

export const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const HeaderTopRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: ${tablet}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
`;

export const TitleSection = styled.div`
  flex: 1;
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #4a4d5e;
`;

export const ShortText = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #6b6b76;
`;

export const InterestCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (min-width: ${tablet}px) {
    align-self: flex-start;
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #1a1a2e;
  user-select: none;
  background-color: #ffffff;
  padding: 8px 12px;
  border-radius: 8px;

  &:hover {
    color: #798ecf;
  }
`;

export const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

export const CustomCheckbox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 2px solid ${({ $checked }) => ($checked ? "#696E8C" : "#b6b6c1")};
  border-radius: 4px;
  background-color: ${({ $checked }) => ($checked ? "#696E8C" : "#ffffff")};
  transition: all 0.2s ease;

  svg {
    display: ${({ $checked }) => ($checked ? "block" : "none")};
    width: 16px;
    height: 16px;
    color: #ffffff;
  }

  &:hover {
    border-color: #696E8C;
  }
`;

// Content Section
export const ContentSection = styled.div`
  padding: 16px;

  @media (min-width: ${tablet}px) {
    padding: 20px 24px;
  }
`;

export const NextStepsHeaderSection = styled.div`
  margin-bottom: 20px;
  padding: 16px 20px;
  background-color: #f0f0f0;
  border-radius: 8px;

  @media (min-width: ${tablet}px) {
    padding: 20px 24px;
  }
`;

export const NextStepsHeaderTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #000000;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const NextStepsArrowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #e0e0e0;
  border-radius: 50%;
  color: #4a4d5e;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const NextStepsHeaderContent = styled.div`
  margin-top: 12px;
  /* Align content with title text by adding left margin equal to icon width + gap */
  margin-left: 48px;

  /* Style the HTML content - Body: 16px / 500 */
  p {
    margin: 0 0 12px 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.6;
    color: #4a4d5e;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 12px 0;
    padding-left: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin-bottom: 6px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    color: #4a4d5e;
  }

  a {
    color: #798ecf;
    text-decoration: underline;

    &:hover {
      color: #5a6fb0;
    }
  }

  strong,
  b {
    font-weight: 600;
  }
`;

export const CallToActionSection = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f9f9fb;
  border-radius: 8px;
  border-left: 4px solid #798ecf;
`;

export const CallToActionText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #4a4d5e;
  line-height: 1.6;

  @media (min-width: ${tablet}px) {
    font-size: 16px;
  }
`;

// Risk Factors Section
export const RiskFactorsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: ${tablet}px) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
`;

export const RiskFactorBox = styled.div<{
  $type?: "increasing" | "decreasing";
}>`
  border-radius: 8px;
  padding: 16px;
  border: none;

  background-color: ${({ $type }) => {
    switch ($type) {
      case "increasing":
        return "#F6EEEF";
      case "decreasing":
        return "#F1F4F0";
      default:
        return "#f9f9fb";
    }
  }};
`;

export const RiskFactorHeader = styled.h4<{
  $type: "increasing" | "decreasing";
}>`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #4a4d5e;
  display: flex;
  align-items: center;
  gap: 8px;

  b {
    color: ${({ $type }) =>
      $type === "increasing" ? "#af021c" : "#478c3b"};
  }
`;

export const RiskFactorIcon = styled.span<{
  $type: "increasing" | "decreasing";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${({ $type }) =>
    $type === "increasing" ? "#af021c" : "#478c3b"};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const RiskFactorList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

export const RiskFactorItemIncreasing = styled.li`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  padding-left: 20px;
  margin-bottom: 8px;
  color: #6b6b76;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #af021c;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const RiskFactorItemDecreasing = styled.li`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  padding-left: 20px;
  margin-bottom: 8px;
  color: #6b6b76;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #478c3b;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const EmptyFactors = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #6b6b76;
  font-style: italic;
`;

// Video Section
export const VideosSection = styled.div`
  margin-top: 24px;
`;

export const VideosSectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #4a4d5e;

  @media (min-width: ${tablet}px) {
    font-size: 18px;
  }
`;

export const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: ${tablet}px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
  }
`;

export const VideoItem = styled.div`
  border: 1px solid #dedee3;
  border-radius: 8px;
  overflow: hidden;
`;

export const VideoWrapper = styled.div`
  position: relative;
  overflow: hidden;

  video {
    width: 100%;
    display: block;
  }
`;

export const VideoTitle = styled.h5`
  margin: 0;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #4a4d5e;
  background-color: #f9f9fb;
  border-top: 1px solid #dedee3;
`;

// Collapsible Section
export const CollapsibleSection = styled.div`
  margin-top: 24px;
`;

export const CollapsibleDivider = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  margin-top: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #696E8C;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const CollapsibleDividerText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #696E8C;

  ${CollapsibleDivider}:hover & {
    text-decoration: underline;
  }
`;

export const CollapsibleDividerIcon = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: #696E8C;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => ($expanded ? "rotate(180deg)" : "rotate(0deg)")};

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const CollapsibleContent = styled.div<{ $expanded: boolean }>`
  display: ${({ $expanded }) => ($expanded ? "block" : "none")};
  padding: 20px 0 0 0;
`;

export const BodyHtmlSection = styled.div`
  margin-bottom: 24px;
  padding: 0 4px;

  /* Style the HTML content - Body: 16px / 500 */
  p {
    margin: 0 0 12px 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.6;
    color: #4a4d5e;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 12px 0;
    padding-left: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin-bottom: 6px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    color: #4a4d5e;
  }

  a {
    color: #798ecf;
    text-decoration: underline;

    &:hover {
      color: #5a6fb0;
    }
  }

  strong,
  b {
    font-weight: 600;
  }
`;

// Content Items Section
export const ContentItemsSection = styled.div`
  margin-top: 24px;
`;

export const ContentItemWrapper = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background-color: transparent;
  border: 1px solid #dedee3;
  border-radius: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ContentItemHtml = styled.div`
  margin-bottom: 16px;

  /* Style the HTML content - Body: 16px / 500 */
  p {
    margin: 0 0 12px 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.6;
    color: #4a4d5e;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 12px 0;
    padding-left: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin-bottom: 6px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    color: #4a4d5e;
  }

  a {
    color: #696E8C;
    text-decoration: underline;

    &:hover {
      color: #4a4d5e;
    }
  }

  strong,
  b {
    font-weight: 600;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ContentItemVideo = styled.div`
  border: 1px solid #dedee3;
  border-radius: 8px;
  overflow: hidden;

  video {
    width: 100%;
    display: block;
  }
`;

export const ContentItemVideoTitle = styled.h5`
  margin: 0;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #4a4d5e;
  background-color: #f0f0f0;
  border-top: 1px solid #dedee3;
`;
